import React, { useState } from 'react';
import type { ServiceStatus } from '../services/patientsService';
import { RobotIcon, BellIcon, MoonIcon, MailSimpleIcon } from '../assets/icons';

interface ServiceStatusIconsProps {
  status: ServiceStatus | null | undefined;
  weeklyDigestEnabled?: boolean;
  size?: number;
  gap?: number;
}

type IconKey = 'agent' | 'push' | 'sleep' | 'digest';

interface IconBadge {
  color: string;
  border: string;
}

interface IconItem {
  key: IconKey;
  color: string;
  background: string;
  border: string;
  label: string;
  description: string;
  badge?: IconBadge;
}

const ICON_RENDER: Record<IconKey, React.FC<{ size: number; color: string }>> = {
  agent: RobotIcon,
  push: BellIcon,
  sleep: MoonIcon,
  digest: MailSimpleIcon,
};

const buildIcons = (status: ServiceStatus): IconItem[] => {
  const agent: IconItem = (() => {
    const badge: IconBadge | undefined = status.insightsEnabled
      ? { color: '#7c3aed', border: '#fff' }
      : undefined;
    const insightsNote = status.insightsEnabled
      ? ' Además, los insights están activados para este paciente.'
      : '';
    if (!status.agentEnabled) {
      return {
        key: 'agent',
        color: '#9ca3af',
        background: '#f3f4f6',
        border: '#e5e7eb',
        label: 'Agente deshabilitado',
        description: 'El agente no está habilitado para este paciente.' + insightsNote,
        badge,
      };
    }
    if (!status.agentProactiveEnabled) {
      return {
        key: 'agent',
        color: '#b45309',
        background: '#fef3c7',
        border: '#fcd34d',
        label: 'Agente habilitado, proactivo deshabilitado',
        description: 'El chat puede ejecutar acciones, pero el sistema no enviará mensajes proactivos.' + insightsNote,
        badge,
      };
    }
    return {
      key: 'agent',
      color: '#047857',
      background: '#d1fae5',
      border: '#6ee7b7',
      label: 'Agente habilitado y proactivo',
      description: 'El chat ejecuta acciones y el sistema puede enviar mensajes proactivos al paciente.' + insightsNote,
      badge,
    };
  })();

  const push: IconItem = status.pushNotifications
    ? {
        key: 'push',
        color: '#047857',
        background: '#d1fae5',
        border: '#6ee7b7',
        label: 'Notificaciones push activas',
        description: 'El paciente puede recibir notificaciones push en su dispositivo.',
      }
    : {
        key: 'push',
        color: '#9ca3af',
        background: '#f3f4f6',
        border: '#e5e7eb',
        label: 'Notificaciones push inactivas',
        description: 'El paciente no tiene notificaciones push activadas.',
      };

  const sleep: IconItem = status.sleepEnabled
    ? {
        key: 'sleep',
        color: '#047857',
        background: '#d1fae5',
        border: '#6ee7b7',
        label: 'Seguimiento de sueño activo',
        description: 'El seguimiento de sueño está habilitado para este paciente.',
      }
    : {
        key: 'sleep',
        color: '#9ca3af',
        background: '#f3f4f6',
        border: '#e5e7eb',
        label: 'Seguimiento de sueño inactivo',
        description: 'El seguimiento de sueño no está habilitado.',
      };

  return [agent, push, sleep];
};

const buildDigestIcon = (enabled: boolean): IconItem =>
  enabled
    ? {
        key: 'digest',
        color: '#047857',
        background: '#d1fae5',
        border: '#6ee7b7',
        label: 'Resumen semanal activo',
        description: 'El paciente recibe por correo un resumen de su progreso cada semana.',
      }
    : {
        key: 'digest',
        color: '#9ca3af',
        background: '#f3f4f6',
        border: '#e5e7eb',
        label: 'Resumen semanal inactivo',
        description: 'El paciente no recibe el correo de resumen semanal.',
      };

const Icon: React.FC<{ item: IconItem; size: number }> = ({ item, size }) => {
  const [hover, setHover] = useState(false);
  const Svg = ICON_RENDER[item.key];
  const inner = Math.round(size * 0.62);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span
        aria-label={item.label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          borderRadius: '50%',
          background: item.background,
          border: `1px solid ${item.border}`,
          color: item.color,
        }}
      >
        <Svg size={inner} color={item.color} />
      </span>
      {item.badge && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: Math.max(8, Math.round(size * 0.3)),
            height: Math.max(8, Math.round(size * 0.3)),
            borderRadius: '50%',
            background: item.badge.color,
            border: `1.5px solid ${item.badge.border}`,
            boxSizing: 'border-box',
          }}
        />
      )}
      {hover && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: `calc(100% + 8px)`,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1f2937',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1.4,
            padding: '8px 10px',
            borderRadius: 6,
            whiteSpace: 'normal',
            width: 220,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            zIndex: 50,
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
          <div style={{ opacity: 0.85 }}>{item.description}</div>
        </span>
      )}
    </span>
  );
};

const ServiceStatusIcons: React.FC<ServiceStatusIconsProps> = ({ status, weeklyDigestEnabled, size = 28, gap = 8 }) => {
  if (!status) {
    return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  }
  const icons = buildIcons(status);
  if (weeklyDigestEnabled !== undefined) {
    icons.push(buildDigestIcon(weeklyDigestEnabled));
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap }}>
      {icons.map(item => (
        <Icon key={item.key} item={item} size={size} />
      ))}
    </span>
  );
};

export default ServiceStatusIcons;
